/**
 * Smart Importer schemas (screen 16, M10) — the SINGLE source of validation for
 * the import pipeline tables, shared by the backoffice and (future) edge
 * functions:
 *  - imports            — one import run (entity_type, source, mapping, strategy,
 *                         status counters).
 *  - import_rows        — one source row (raw + mapped payloads, validation
 *                         status/messages, the outcome — created / updated /
 *                         unchanged / skipped / error — and the entity it created
 *                         or matched on commit).
 *  - ai_mapping_memory  — remembered column→target mappings per tenant, recalled
 *                         on a re-upload of the same export and bumped on save.
 *
 * KEY INVARIANT — multitenancy: `tenant_id` is NEVER accepted from client input
 * (RLS + the DB DEFAULT inject it). The *Input schemas omit it; `.strict()`
 * rejects any attempt to set it. These tables are ADMIN-ONLY at the RLS layer.
 */

import { z } from 'zod';
import {
  IMPORT_ENTITY_TYPES,
  IMPORT_STATUSES,
  IMPORT_ROW_STATUSES,
  IMPORT_ROW_ACTIONS,
  IMPORT_MERGE_STRATEGIES,
} from '../enums/index.js';
import { uuid, nonEmptyString, timestamptz, jsonObject } from './common.js';

// ── imports ─────────────────────────────────────────────────────────────────

/**
 * Create an import run. `mapping_json` is the chosen source-column → target-field
 * map (recorded for audit + re-recall). The client never sets tenant_id/status
 * counters that the server owns; status defaults to 'uploaded' and is advanced as
 * the wizard progresses.
 */
export const ImportCreateInput = z
  .object({
    entity_type: z.enum(IMPORT_ENTITY_TYPES),
    source_filename: nonEmptyString.nullable().optional(),
    mapping_json: jsonObject.default({}),
    merge_strategy: z.enum(IMPORT_MERGE_STRATEGIES).default('create_only'),
    status: z.enum(IMPORT_STATUSES).default('uploaded'),
    total_rows: z.number().int().nonnegative().nullable().optional(),
    error_count: z.number().int().nonnegative().nullable().optional(),
  })
  .strict();
export type ImportCreateInput = z.infer<typeof ImportCreateInput>;

/** Patch an existing import run (advance status, set counters, confirm). */
export const ImportUpdateInput = z
  .object({
    mapping_json: jsonObject.optional(),
    merge_strategy: z.enum(IMPORT_MERGE_STRATEGIES).optional(),
    status: z.enum(IMPORT_STATUSES).optional(),
    total_rows: z.number().int().nonnegative().nullable().optional(),
    error_count: z.number().int().nonnegative().nullable().optional(),
    /**
     * Outcome counters, written when the run is confirmed. Together with
     * error_count they reconstruct the result screen from the database alone:
     * created + updated + unchanged + skipped + error = total_rows.
     */
    created_count: z.number().int().nonnegative().nullable().optional(),
    updated_count: z.number().int().nonnegative().nullable().optional(),
    unchanged_count: z.number().int().nonnegative().nullable().optional(),
    skipped_count: z.number().int().nonnegative().nullable().optional(),
    confirmed_at: timestamptz.nullable().optional(),
  })
  .strict();
export type ImportUpdateInput = z.infer<typeof ImportUpdateInput>;

/** Persisted imports row (read shape). */
export const Import = z
  .object({
    id: uuid,
    tenant_id: uuid,
    entity_type: z.enum(IMPORT_ENTITY_TYPES),
    source_filename: z.string().nullable(),
    source_url: z.string().nullable(),
    mapping_json: jsonObject,
    merge_strategy: z.enum(IMPORT_MERGE_STRATEGIES),
    status: z.enum(IMPORT_STATUSES),
    total_rows: z.number().int().nullable(),
    error_count: z.number().int().nullable(),
    created_count: z.number().int().nullable(),
    updated_count: z.number().int().nullable(),
    unchanged_count: z.number().int().nullable(),
    skipped_count: z.number().int().nullable(),
    confirmed_at: timestamptz.nullable(),
    created_at: timestamptz,
    created_by: uuid.nullable(),
    updated_at: timestamptz,
  })
  .strict();
export type Import = z.infer<typeof Import>;

// ── import_rows ─────────────────────────────────────────────────────────────

/**
 * One source row of an import run. `raw_data_json` is the source record keyed by
 * header; `mapped_data_json` is the projected target payload; the validation
 * status + messages drive the preview. `import_id` ties it to the run; tenant_id
 * is injected server-side.
 */
export const ImportRowInput = z
  .object({
    import_id: uuid,
    /**
     * La pestanya del full d'origen, quan el fitxer en porta més d'una (el
     * client importer: clients i adreces d'enviament). Amb `row_number` forma la
     * identitat de la fila dins del run, de manera que dues pestanyes que
     * comencen per la fila 1 no es trepitgin.
     */
    source_sheet: nonEmptyString.nullable().optional(),
    row_number: z.number().int().nonnegative(),
    raw_data_json: jsonObject,
    mapped_data_json: jsonObject.nullable().optional(),
    validation_status: z.enum(IMPORT_ROW_STATUSES).default('pending'),
    validation_messages: z.array(z.string()).default([]),
    /**
     * The planned (at preview) then actual (at commit) outcome for this row.
     * Left unset while the outcome is not yet known.
     */
    row_action: z.enum(IMPORT_ROW_ACTIONS).nullable().optional(),
    /** The EXISTING entity this row matched (updated / unchanged / skipped). */
    matched_entity_id: uuid.nullable().optional(),
  })
  .strict();
export type ImportRowInput = z.infer<typeof ImportRowInput>;

/** Patch an import_row after a real write (status + the created entity id). */
export const ImportRowUpdateInput = z
  .object({
    validation_status: z.enum(IMPORT_ROW_STATUSES).optional(),
    validation_messages: z.array(z.string()).optional(),
    row_action: z.enum(IMPORT_ROW_ACTIONS).nullable().optional(),
    /** Only ever set for a row that CREATED an entity. */
    created_entity_id: uuid.nullable().optional(),
    /** Only ever set for a row that matched an EXISTING entity. */
    matched_entity_id: uuid.nullable().optional(),
  })
  .strict();
export type ImportRowUpdateInput = z.infer<typeof ImportRowUpdateInput>;

/** Persisted import_rows row (read shape). */
export const ImportRow = z
  .object({
    id: uuid,
    tenant_id: uuid,
    import_id: uuid,
    source_sheet: z.string().nullable(),
    row_number: z.number().int(),
    raw_data_json: jsonObject,
    mapped_data_json: jsonObject.nullable(),
    validation_status: z.enum(IMPORT_ROW_STATUSES),
    validation_messages: z.array(z.unknown()),
    row_action: z.enum(IMPORT_ROW_ACTIONS).nullable(),
    created_entity_id: uuid.nullable(),
    matched_entity_id: uuid.nullable(),
    created_at: timestamptz,
  })
  .strict();
export type ImportRow = z.infer<typeof ImportRow>;

// ── ai_mapping_memory ───────────────────────────────────────────────────────

/**
 * Upsert a remembered column mapping. `source_key` is the obrador's raw header
 * label; `target_field` is the logical Article field it was mapped to. The
 * (tenant_id, entity_type, source_key, target_field) tuple is unique server-side,
 * so a re-save bumps hit_count + last_used_at (handled in the data layer).
 */
export const AiMappingMemoryUpsertInput = z
  .object({
    entity_type: z.enum(IMPORT_ENTITY_TYPES),
    source_key: nonEmptyString,
    target_field: nonEmptyString,
    target_value: z.string().nullable().optional(),
    confidence: z.number().finite().min(0).max(1).optional(),
    hit_count: z.number().int().min(1).optional(),
    last_used_at: timestamptz.nullable().optional(),
  })
  .strict();
export type AiMappingMemoryUpsertInput = z.infer<typeof AiMappingMemoryUpsertInput>;

/** Persisted ai_mapping_memory row (read shape). */
export const AiMappingMemory = z
  .object({
    id: uuid,
    tenant_id: uuid,
    entity_type: z.enum(IMPORT_ENTITY_TYPES),
    source_key: z.string(),
    target_field: z.string(),
    target_value: z.string().nullable(),
    confidence: z.number(),
    hit_count: z.number().int(),
    last_used_at: timestamptz.nullable(),
    created_at: timestamptz,
    updated_at: timestamptz,
  })
  .strict();
export type AiMappingMemory = z.infer<typeof AiMappingMemory>;
